package com.karawek.core.auth.internal

import com.karawek.core.auth.api.AuthSessionStore
import com.karawek.core.auth.di.AuthSessionStorage
import com.karawek.core.auth.model.AuthSession
import com.karawek.core.platform.SecureStorage
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.onStart
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import software.amazon.lastmile.kotlin.inject.anvil.ContributesBinding
import software.amazon.lastmile.kotlin.inject.anvil.SingleIn

private const val KEY_USER_ID = "auth.user_id"
private const val KEY_SESSION_TOKEN = "auth.session_token"
private const val KEY_SESSION_JWT = "auth.session_jwt"
private const val KEY_SESSION_ID = "auth.session_id"
private const val KEY_SESSION_EXPIRATION = "auth.session_expiration"

@SingleIn(AppScope::class)
@ContributesBinding(AppScope::class)
@Inject
class DefaultAuthSessionStore(
    @param:AuthSessionStorage private val secureStorage: SecureStorage,
) : AuthSessionStore {

    private val sessionChanges = MutableSharedFlow<AuthSession?>(replay = 1)

    override suspend fun read(): AuthSession? {
        val userId = secureStorage.string(KEY_USER_ID)
            ?: return null

        val sessionToken = secureStorage.string(KEY_SESSION_TOKEN)
            ?: return null

        return AuthSession(
            userId = userId,
            sessionToken = sessionToken,
            sessionJwt = secureStorage.string(KEY_SESSION_JWT),
            sessionId = secureStorage.string(KEY_SESSION_ID),
            sessionExpiration = secureStorage.instant(KEY_SESSION_EXPIRATION),
        )
    }

    override fun observe(): Flow<AuthSession?> =
        sessionChanges.onStart { emit(read()) }

    override suspend fun write(session: AuthSession) {
        secureStorage.set(KEY_USER_ID, session.userId)
        secureStorage.set(KEY_SESSION_TOKEN, session.sessionToken)

        session.sessionJwt?.let {
            secureStorage.set(KEY_SESSION_JWT, it)
        } ?: secureStorage.deleteObject(KEY_SESSION_JWT)

        session.sessionId?.let {
            secureStorage.set(KEY_SESSION_ID, it)
        } ?: secureStorage.deleteObject(KEY_SESSION_ID)

        session.sessionExpiration?.let {
            secureStorage.set(KEY_SESSION_EXPIRATION, it)
        } ?: secureStorage.deleteObject(KEY_SESSION_EXPIRATION)
        
        sessionChanges.emit(session)
    }

    override suspend fun clear() {
        secureStorage.deleteObject(KEY_USER_ID)
        secureStorage.deleteObject(KEY_SESSION_TOKEN)
        secureStorage.deleteObject(KEY_SESSION_JWT)
        secureStorage.deleteObject(KEY_SESSION_ID)
        secureStorage.deleteObject(KEY_SESSION_EXPIRATION)
        
        sessionChanges.emit(null)
    }
}
